/**
 * @module contracts
 * @description SDK-free adapter contracts for infrastructure domains. Concrete
 * provider code lives outside this package except for the memory dev/test
 * adapters.
 */

import { z } from "zod";

import { AdapterError } from "./errors";

const STORAGE_MAX_LIST_LIMIT = 1000;
const STORAGE_MAX_TTL_SECONDS = 60 * 60 * 24 * 365;
const STORAGE_MAX_OBJECT_BODY_BYTES = 2 * 1024 * 1024;
const CACHE_MAX_TTL_SECONDS = 60 * 60 * 24 * 365;

const getStringByteLength = (value: string): number =>
  new TextEncoder().encode(value).byteLength;

/**
 * JSON-compatible value accepted by SDK-free adapter contracts. Use this for
 * payloads that must round-trip through local/dev adapters and provider
 * contracts without preserving class instances or cyclic references.
 *
 * @example
 * ```ts
 * const payload: AdapterJsonValue = { feature: "cache", enabled: true };
 * ```
 */
export type AdapterJsonValue =
  | string
  | number
  | boolean
  | null
  | AdapterJsonValue[]
  | { [key: string]: AdapterJsonValue };

function isPlainJsonRecord(value: object): value is Record<string, unknown> {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isAdapterJsonValue(
  value: unknown,
  stack = new WeakSet<object>(),
): value is AdapterJsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value !== "object") {
    return false;
  }

  if (stack.has(value)) {
    return false;
  }

  stack.add(value);

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      if (!(index in value) || !isAdapterJsonValue(value[index], stack)) {
        stack.delete(value);
        return false;
      }
    }
    stack.delete(value);
    return true;
  }

  if (!isPlainJsonRecord(value) || Object.getOwnPropertySymbols(value).length) {
    stack.delete(value);
    return false;
  }

  const descriptors = Object.getOwnPropertyDescriptors(value);
  for (const descriptor of Object.values(descriptors)) {
    if (
      !descriptor.enumerable ||
      !("value" in descriptor) ||
      !isAdapterJsonValue(descriptor.value, stack)
    ) {
      stack.delete(value);
      return false;
    }
  }

  stack.delete(value);
  return true;
}

/**
 * Runtime validator for JSON-compatible values passed through adapter
 * boundaries. Use this before writing cache values, queue payloads, or event
 * envelopes supplied by application code.
 *
 * @example
 * ```ts
 * const payload = AdapterJsonValueSchema.parse({ retry: 2 });
 * ```
 */
export const AdapterJsonValueSchema: z.ZodType<AdapterJsonValue> =
  z.custom<AdapterJsonValue>((value) => isAdapterJsonValue(value), {
    message:
      "Expected a JSON-compatible primitive, array, or plain object without cycles",
  });

const IsoDateTimeStringSchema = z.string().datetime({ offset: true });
const ISO_4217_CURRENCY_CODE = /^[A-Z]{3}$/;

/**
 * Launch backend ids supported by the adapter contract matrix. Use this tuple
 * when building parity tests or provider pickers that must stay aligned with
 * the handwritten adapter package shape.
 *
 * @example
 * ```ts
 * const launchBackends = [...ADAPTER_CONTRACT_BACKENDS];
 * ```
 */
export const ADAPTER_CONTRACT_BACKENDS = [
  "convex",
  "neon",
  "cloudflareKV",
  "memory",
] as const;

/**
 * Infrastructure domains owned by the contract-only adapter surface. Use this
 * tuple when registering SDK-free domain contracts rather than concrete
 * production providers.
 *
 * @example
 * ```ts
 * const supportsDeploy = ADAPTER_CONTRACT_DOMAINS.includes("deploy");
 * ```
 */
export const ADAPTER_CONTRACT_DOMAINS = [
  "storage",
  "cache",
  "queue",
  "events",
  "auth",
  "payments",
  "deploy",
] as const;

/**
 * Backend id for a launch adapter contract package.
 *
 * @example
 * ```ts
 * const backend: AdapterContractBackend = "memory";
 * ```
 */
export type AdapterContractBackend = (typeof ADAPTER_CONTRACT_BACKENDS)[number];

/**
 * Domain id for the contract-only adapter surface.
 *
 * @example
 * ```ts
 * const domain: AdapterContractDomain = "queue";
 * ```
 */
export type AdapterContractDomain = (typeof ADAPTER_CONTRACT_DOMAINS)[number];

/**
 * Runtime validator for launch backend ids accepted by contract manifests.
 *
 * @example
 * ```ts
 * const backend = AdapterContractBackendSchema.parse("cloudflareKV");
 * ```
 */
export const AdapterContractBackendSchema = z.enum(ADAPTER_CONTRACT_BACKENDS);

/**
 * Runtime validator for contract domain ids accepted by contract manifests.
 *
 * @example
 * ```ts
 * const domain = AdapterContractDomainSchema.parse("events");
 * ```
 */
export const AdapterContractDomainSchema = z.enum(ADAPTER_CONTRACT_DOMAINS);

/**
 * Runtime validator for string metadata maps used by several domains. Use this
 * for provider-neutral metadata that must remain small and serializable.
 *
 * @example
 * ```ts
 * const metadata = AdapterMetadataSchema.parse({ region: "eu" });
 * ```
 */
export const AdapterMetadataSchema = z.record(z.string(), z.string());

/**
 * Runtime validator for file-storage metadata scalar values.
 *
 * @example
 * ```ts
 * const value = FileStorageMetadataValueSchema.parse("invoice");
 * ```
 */
export const FileStorageMetadataValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

/**
 * Runtime validator for caller-controlled file metadata maps. Use this before
 * handing upload metadata to local/dev file storage adapters.
 *
 * @example
 * ```ts
 * const metadata = FileStorageMetadataSchema.parse({ public: true });
 * ```
 */
export const FileStorageMetadataSchema = z.record(
  z.string(),
  FileStorageMetadataValueSchema,
);

/**
 * Common health status returned by adapter implementations. Use this schema to
 * normalize provider health checks before surfacing them in review UI or logs.
 *
 * @example
 * ```ts
 * const health = AdapterHealthSchema.parse({ ok: true, checkedAt: new Date().toISOString() });
 * ```
 */
export const AdapterHealthSchema = z.object({
  ok: z.boolean(),
  checkedAt: IsoDateTimeStringSchema,
  latencyMs: z.number().nonnegative().optional(),
  message: z.string().optional(),
});

/**
 * Common health status type returned by adapter implementations.
 *
 * @example
 * ```ts
 * const health: AdapterHealth = { ok: true, checkedAt: new Date().toISOString() };
 * ```
 */
export type AdapterHealth = z.infer<typeof AdapterHealthSchema>;

/**
 * Shared page options for list operations. Use this schema for cursor, prefix,
 * or bounded limit arguments accepted by contract-only list methods.
 *
 * @example
 * ```ts
 * const options = AdapterListOptionsSchema.parse({ limit: 25, prefix: "prod/" });
 * ```
 */
export const AdapterListOptionsSchema = z.object({
  limit: z.number().int().positive().max(STORAGE_MAX_LIST_LIMIT).optional(),
  cursor: z.string().min(1).optional(),
  prefix: z.string().min(1).optional(),
});

/**
 * Shared page options type for list operations.
 *
 * @example
 * ```ts
 * const options: AdapterListOptions = { limit: 50 };
 * ```
 */
export type AdapterListOptions = z.infer<typeof AdapterListOptionsSchema>;

/**
 * Declarative metadata for a domain/backend contract cell. Use this schema for
 * manifest files and conformance fixtures that describe adapter capabilities.
 *
 * @example
 * ```ts
 * const manifest = AdapterContractManifestSchema.parse({
 *   domain: "cache",
 *   backend: "memory",
 *   provider: "local",
 * });
 * ```
 */
export const AdapterContractManifestSchema = z.object({
  domain: AdapterContractDomainSchema,
  backend: AdapterContractBackendSchema,
  provider: z.string(),
  capabilities: z.array(z.string()).default([]),
});

/**
 * Declarative metadata for a domain/backend contract cell.
 *
 * @example
 * ```ts
 * const manifest: AdapterContractManifest = {
 *   domain: "deploy",
 *   backend: "memory",
 *   provider: "local",
 *   capabilities: [],
 * };
 * ```
 */
export type AdapterContractManifest = z.infer<
  typeof AdapterContractManifestSchema
>;

/**
 * Error raised by SDK-free contract validation helpers. Use this when a
 * contract-layer guard rejects input before any concrete provider SDK runs.
 *
 * @example
 * ```ts
 * throw new AdapterContractError("contract", "Invalid adapter payload");
 * ```
 */
export class AdapterContractError extends AdapterError {
  /**
   * Creates a contract-layer error without coupling to a concrete provider SDK.
   *
   * @param domain - Contract domain that rejected the payload.
   * @param message - Human-readable validation failure.
   * @param cause - Optional underlying parser or provider error.
   * @returns A typed adapter contract error.
   * @example
   * ```ts
   * const error = new AdapterContractError("storage", "Invalid key");
   * ```
   */
  constructor(
    domain: AdapterContractDomain | "contract",
    message: string,
    cause?: unknown,
  ) {
    super(message, domain, "CONTRACT_ERROR", cause);
    this.name = "AdapterContractError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised by storage contract implementations when object/blob input or
 * output violates the SDK-free storage contract.
 *
 * @example
 * ```ts
 * throw new StorageContractError("Storage object key is required");
 * ```
 */
export class StorageContractError extends AdapterContractError {
  /**
   * Creates a storage-specific contract error.
   *
   * @param message - Human-readable storage contract failure.
   * @param cause - Optional parser or provider error that triggered the failure.
   * @returns A typed storage contract error.
   * @example
   * ```ts
   * const error = new StorageContractError("Invalid metadata");
   * ```
   */
  constructor(message: string, cause?: unknown) {
    super("storage", message, cause);
    this.name = "StorageContractError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised by cache contract implementations when cache keys or values do
 * not satisfy the JSON-compatible cache boundary.
 *
 * @example
 * ```ts
 * throw new CacheContractError("Cache value is not JSON-compatible");
 * ```
 */
export class CacheContractError extends AdapterContractError {
  /**
   * Creates a cache-specific contract error.
   *
   * @param message - Human-readable cache contract failure.
   * @param cause - Optional parser or provider error that triggered the failure.
   * @returns A typed cache contract error.
   * @example
   * ```ts
   * const error = new CacheContractError("Invalid TTL");
   * ```
   */
  constructor(message: string, cause?: unknown) {
    super("cache", message, cause);
    this.name = "CacheContractError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised by queue contract implementations when job payloads or state
 * transitions violate the queue contract.
 *
 * @example
 * ```ts
 * throw new QueueContractError("Job id is required");
 * ```
 */
export class QueueContractError extends AdapterContractError {
  /**
   * Creates a queue-specific contract error.
   *
   * @param message - Human-readable queue contract failure.
   * @param cause - Optional parser or provider error that triggered the failure.
   * @returns A typed queue contract error.
   * @example
   * ```ts
   * const error = new QueueContractError("Invalid scheduled run time");
   * ```
   */
  constructor(message: string, cause?: unknown) {
    super("queue", message, cause);
    this.name = "QueueContractError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised by events contract implementations when pub/sub or webhook
 * payloads violate the event boundary.
 *
 * @example
 * ```ts
 * throw new EventsContractError("Webhook URL is invalid");
 * ```
 */
export class EventsContractError extends AdapterContractError {
  /**
   * Creates an events-specific contract error.
   *
   * @param message - Human-readable events contract failure.
   * @param cause - Optional parser or provider error that triggered the failure.
   * @returns A typed events contract error.
   * @example
   * ```ts
   * const error = new EventsContractError("Invalid topic");
   * ```
   */
  constructor(message: string, cause?: unknown) {
    super("events", message, cause);
    this.name = "EventsContractError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised by auth contract implementations when identity or session
 * records violate the contract-only auth boundary.
 *
 * @example
 * ```ts
 * throw new AuthContractError("Session token is required");
 * ```
 */
export class AuthContractError extends AdapterContractError {
  /**
   * Creates an auth-specific contract error.
   *
   * @param message - Human-readable auth contract failure.
   * @param cause - Optional parser or provider error that triggered the failure.
   * @returns A typed auth contract error.
   * @example
   * ```ts
   * const error = new AuthContractError("Invalid identity");
   * ```
   */
  constructor(message: string, cause?: unknown) {
    super("auth", message, cause);
    this.name = "AuthContractError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised by payments contract implementations when plan, checkout, or
 * subscription records violate the provider-neutral payments contract.
 *
 * @example
 * ```ts
 * throw new PaymentsContractError("Plan currency must be ISO 4217");
 * ```
 */
export class PaymentsContractError extends AdapterContractError {
  /**
   * Creates a payments-specific contract error.
   *
   * @param message - Human-readable payments contract failure.
   * @param cause - Optional parser or provider error that triggered the failure.
   * @returns A typed payments contract error.
   * @example
   * ```ts
   * const error = new PaymentsContractError("Invalid checkout session");
   * ```
   */
  constructor(message: string, cause?: unknown) {
    super("payments", message, cause);
    this.name = "PaymentsContractError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error raised by deployment contract implementations when deployment targets
 * or results violate the SDK-free deploy boundary.
 *
 * @example
 * ```ts
 * throw new DeployContractError("Deployment target provider is invalid");
 * ```
 */
export class DeployContractError extends AdapterContractError {
  /**
   * Creates a deploy-specific contract error.
   *
   * @param message - Human-readable deploy contract failure.
   * @param cause - Optional parser or provider error that triggered the failure.
   * @returns A typed deploy contract error.
   * @example
   * ```ts
   * const error = new DeployContractError("Invalid deployment result");
   * ```
   */
  constructor(message: string, cause?: unknown) {
    super("deploy", message, cause);
    this.name = "DeployContractError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Serializable object stored by storage adapters. Use this schema to validate
 * local/dev object storage reads before returning them to consumers.
 *
 * @example
 * ```ts
 * const object = StorageObjectSchema.parse({ key: "a.txt", body: "ok", size: 2, updatedAt: new Date().toISOString() });
 * ```
 */
export const StorageObjectSchema = z.object({
  key: z.string().min(1),
  body: z.string(),
  contentType: z.string().optional(),
  size: z.number().int().nonnegative(),
  etag: z.string().optional(),
  metadata: AdapterMetadataSchema.default({}),
  updatedAt: IsoDateTimeStringSchema,
});

/**
 * Serializable object stored by storage adapters.
 *
 * @example
 * ```ts
 * const object: StorageObject = { key: "a.txt", body: "ok", size: 2, metadata: {}, updatedAt: new Date().toISOString() };
 * ```
 */
export type StorageObject = z.infer<typeof StorageObjectSchema>;

/**
 * Write payload accepted by storage adapters. Use this schema before persisting
 * object bodies through memory or provider-bound storage implementations.
 *
 * @example
 * ```ts
 * const input = StorageWriteInputSchema.parse({ key: "a.txt", body: "ok" });
 * ```
 */
export const StorageWriteInputSchema = z.object({
  key: z.string().min(1),
  body: z
    .string()
    .refine(
      (body) => getStringByteLength(body) <= STORAGE_MAX_OBJECT_BODY_BYTES,
      "Storage object body exceeds the 2 MiB contract limit",
    ),
  contentType: z.string().min(1).optional(),
  metadata: AdapterMetadataSchema.optional(),
  ttlSeconds: z
    .number()
    .int()
    .positive()
    .max(STORAGE_MAX_TTL_SECONDS)
    .optional(),
});

/**
 * Write payload accepted by storage adapters.
 *
 * @example
 * ```ts
 * const input: StorageWriteInput = { key: "a.txt", body: "ok" };
 * ```
 */
export type StorageWriteInput = z.infer<typeof StorageWriteInputSchema>;

/**
 * Upload options accepted by file storage adapters. Use this for metadata-only
 * upload settings that remain independent of provider SDK upload types.
 *
 * @example
 * ```ts
 * const options = FileUploadOptionsSchema.parse({ name: "avatar.png" });
 * ```
 */
export const FileUploadOptionsSchema = z.object({
  name: z.string().min(1).optional(),
  metadata: FileStorageMetadataSchema.optional(),
});

/**
 * Runtime validator for file records returned by file storage adapters.
 *
 * @example
 * ```ts
 * const file = StoredFileSchema.parse({ id: "1", name: "a.txt", path: "/a.txt", size: 2, mimeType: "text/plain", url: "memory://a.txt", createdAt: new Date().toISOString() });
 * ```
 */
export const StoredFileSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  path: z.string(),
  size: z.number().int().nonnegative(),
  mimeType: z.string().min(1),
  url: z.string().min(1),
  metadata: FileStorageMetadataSchema.optional(),
  createdAt: IsoDateTimeStringSchema,
});

/**
 * Contract for object/blob storage providers. Implement this in local/dev
 * storage adapters or downstream provider modules that own concrete SDKs.
 *
 * @example
 * ```ts
 * const object = await storage.getObject("a.txt");
 * ```
 */
export interface StorageAdapter {
  getObject(key: string): Promise<StorageObject | null>;
  putObject(input: StorageWriteInput): Promise<StorageObject>;
  deleteObject(key: string): Promise<void>;
  listObjects(options?: AdapterListOptions): Promise<StorageObject[]>;
  getHealth(): Promise<AdapterHealth>;
}

/**
 * Serializable cache entry. Use this schema for cache reads that need to
 * preserve expiry metadata alongside JSON-compatible values.
 *
 * @example
 * ```ts
 * const entry = CacheEntrySchema.parse({ key: "flag", value: true, updatedAt: new Date().toISOString() });
 * ```
 */
export const CacheEntrySchema = z.object({
  key: z.string().min(1),
  value: AdapterJsonValueSchema,
  expiresAt: IsoDateTimeStringSchema.optional(),
  updatedAt: IsoDateTimeStringSchema,
});

/**
 * Serializable cache entry.
 *
 * @example
 * ```ts
 * const entry: CacheEntry = { key: "flag", value: true, updatedAt: new Date().toISOString() };
 * ```
 */
export type CacheEntry = z.infer<typeof CacheEntrySchema>;

/**
 * Cache write options. Use this schema when accepting caller-provided TTLs for
 * memory, Redis, or edge KV cache implementations.
 *
 * @example
 * ```ts
 * const options = CacheSetOptionsSchema.parse({ ttlSeconds: 60 });
 * ```
 */
export const CacheSetOptionsSchema = z.object({
  ttlSeconds: z.number().int().positive().max(CACHE_MAX_TTL_SECONDS).optional(),
});

/**
 * Cache write options.
 *
 * @example
 * ```ts
 * const options: CacheSetOptions = { ttlSeconds: 60 };
 * ```
 */
export type CacheSetOptions = z.infer<typeof CacheSetOptionsSchema>;

/**
 * Contract for key/value cache providers. Implement this for local memory,
 * Redis, or KV-backed caches while keeping values JSON-compatible.
 *
 * @example
 * ```ts
 * await cache.set("feature", true, { ttlSeconds: 60 });
 * ```
 */
export interface CacheAdapter {
  get<T extends AdapterJsonValue = AdapterJsonValue>(
    key: string,
  ): Promise<T | null>;
  set<T extends AdapterJsonValue = AdapterJsonValue>(
    key: string,
    value: T,
    options?: CacheSetOptions,
  ): Promise<void>;
  delete(key: string): Promise<void>;
  has(key: string): Promise<boolean>;
  clear(prefix?: string): Promise<void>;
  getHealth(): Promise<AdapterHealth>;
}

/**
 * Queue job status values accepted by the scheduled-work contract.
 *
 * @example
 * ```ts
 * const status = QueueJobStatusSchema.parse("queued");
 * ```
 */
export const QueueJobStatusSchema = z.enum([
  "queued",
  "running",
  "completed",
  "failed",
  "scheduled",
]);

/**
 * Queue job status values accepted by the scheduled-work contract.
 *
 * @example
 * ```ts
 * const status: QueueJobStatus = "scheduled";
 * ```
 */
export type QueueJobStatus = z.infer<typeof QueueJobStatusSchema>;

/**
 * Serializable queue job. Use this schema for queue state returned by local or
 * provider-backed queue adapters.
 *
 * @example
 * ```ts
 * const job = QueueJobSchema.parse({ id: "1", name: "sync", payload: {}, status: "queued", attempts: 0, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
 * ```
 */
export const QueueJobSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  payload: AdapterJsonValueSchema,
  status: QueueJobStatusSchema,
  runAt: IsoDateTimeStringSchema.optional(),
  attempts: z.number().int().nonnegative(),
  createdAt: IsoDateTimeStringSchema,
  updatedAt: IsoDateTimeStringSchema,
});

/**
 * Serializable queue job.
 *
 * @example
 * ```ts
 * const job: QueueJob = { id: "1", name: "sync", payload: {}, status: "queued", attempts: 0, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
 * ```
 */
export type QueueJob = z.infer<typeof QueueJobSchema>;

/**
 * Queue enqueue payload. Use this schema before accepting work from callers or
 * conformance fixtures.
 *
 * @example
 * ```ts
 * const input = QueueJobInputSchema.parse({ name: "sync", payload: { id: 1 } });
 * ```
 */
export const QueueJobInputSchema = z.object({
  name: z.string().min(1),
  payload: AdapterJsonValueSchema.optional(),
  runAt: IsoDateTimeStringSchema.optional(),
});

/**
 * Queue enqueue payload.
 *
 * @example
 * ```ts
 * const input: QueueJobInput = { name: "sync", payload: { id: 1 } };
 * ```
 */
export type QueueJobInput = z.infer<typeof QueueJobInputSchema>;

/**
 * Contract for queue and scheduled-work providers. Implement this for local
 * test queues or downstream queue providers that own runtime SDKs.
 *
 * @example
 * ```ts
 * const job = await queue.enqueue({ name: "sync" });
 * ```
 */
export interface QueueAdapter {
  enqueue(input: QueueJobInput): Promise<QueueJob>;
  schedule(input: QueueJobInput & { runAt: string }): Promise<QueueJob>;
  complete(jobId: string): Promise<void>;
  fail(jobId: string, reason?: string): Promise<void>;
  listJobs(options?: AdapterListOptions): Promise<QueueJob[]>;
  getHealth(): Promise<AdapterHealth>;
}

/**
 * Serializable event envelope. Use this schema to validate pub/sub messages
 * before they cross webhook or provider adapter boundaries.
 *
 * @example
 * ```ts
 * const event = EventEnvelopeSchema.parse({ id: "1", topic: "user.created", payload: {}, createdAt: new Date().toISOString() });
 * ```
 */
export const EventEnvelopeSchema = z.object({
  id: z.string().min(1),
  topic: z.string().min(1),
  payload: AdapterJsonValueSchema,
  metadata: AdapterMetadataSchema.default({}),
  createdAt: IsoDateTimeStringSchema,
});

/**
 * Serializable event envelope.
 *
 * @example
 * ```ts
 * const event: EventEnvelope = { id: "1", topic: "user.created", payload: {}, metadata: {}, createdAt: new Date().toISOString() };
 * ```
 */
export type EventEnvelope = z.infer<typeof EventEnvelopeSchema>;

/**
 * Event publish payload. Use this schema before handing caller-supplied events
 * to local or provider-backed pub/sub adapters.
 *
 * @example
 * ```ts
 * const input = EventPublishInputSchema.parse({ topic: "user.created", payload: { id: "u1" } });
 * ```
 */
export const EventPublishInputSchema = z.object({
  topic: z.string().min(1),
  payload: AdapterJsonValueSchema.optional(),
  metadata: AdapterMetadataSchema.optional(),
});

/**
 * Event publish payload.
 *
 * @example
 * ```ts
 * const input: EventPublishInput = { topic: "user.created", payload: { id: "u1" } };
 * ```
 */
export type EventPublishInput = z.infer<typeof EventPublishInputSchema>;

/**
 * Event subscription payload. Use this schema when a consumer subscribes to a
 * topic without publishing a payload.
 *
 * @example
 * ```ts
 * const input = EventSubscribeInputSchema.parse({ topic: "user.created" });
 * ```
 */
export const EventSubscribeInputSchema = EventPublishInputSchema.pick({
  topic: true,
});

/**
 * Event subscription payload.
 *
 * @example
 * ```ts
 * const input: EventSubscribeInput = { topic: "user.created" };
 * ```
 */
export type EventSubscribeInput = z.infer<typeof EventSubscribeInputSchema>;

/**
 * Webhook delivery record. Use this schema for durable delivery status returned
 * by webhook-capable events adapters.
 *
 * @example
 * ```ts
 * const delivery = WebhookDeliverySchema.parse({ id: "1", url: "https://example.com", eventId: "evt", status: "queued", createdAt: new Date().toISOString() });
 * ```
 */
export const WebhookDeliverySchema = z.object({
  id: z.string().min(1),
  url: z.string().url(),
  eventId: z.string().min(1),
  status: z.enum(["queued", "delivered", "failed"]),
  createdAt: IsoDateTimeStringSchema,
});

/**
 * Webhook delivery record.
 *
 * @example
 * ```ts
 * const delivery: WebhookDelivery = { id: "1", url: "https://example.com", eventId: "evt", status: "queued", createdAt: new Date().toISOString() };
 * ```
 */
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>;

/**
 * Webhook emit payload. Use this schema before dispatching an event envelope to
 * a caller-provided webhook URL.
 *
 * @example
 * ```ts
 * const input = WebhookEmitInputSchema.parse({ url: "https://example.com", event });
 * ```
 */
export const WebhookEmitInputSchema = z.object({
  url: z.string().url(),
  event: EventEnvelopeSchema,
});

/**
 * Webhook emit payload.
 *
 * @example
 * ```ts
 * const input: WebhookEmitInput = { url: "https://example.com", event };
 * ```
 */
export type WebhookEmitInput = z.infer<typeof WebhookEmitInputSchema>;

/**
 * Unsubscribe handle returned by event subscriptions. Use this to model
 * provider-neutral cleanup for long-lived local or remote subscriptions.
 *
 * @example
 * ```ts
 * await subscription.unsubscribe();
 * ```
 */
export interface EventSubscription {
  unsubscribe(): Promise<void>;
}

/**
 * Contract for pub/sub and webhook providers. Implement this for local event
 * emitters or downstream providers that own broker/webhook SDKs.
 *
 * @example
 * ```ts
 * const event = await events.publish({ topic: "user.created" });
 * ```
 */
export interface EventsAdapter {
  publish(input: EventPublishInput): Promise<EventEnvelope>;
  subscribe(
    input: EventSubscribeInput,
    handler: (event: EventEnvelope) => void | Promise<void>,
  ): Promise<EventSubscription>;
  emitWebhook(input: WebhookEmitInput): Promise<WebhookDelivery>;
  listEvents(options?: AdapterListOptions): Promise<EventEnvelope[]>;
  getHealth(): Promise<AdapterHealth>;
}

/**
 * Identity record used by auth provider adapters. Use this schema for user
 * identity payloads before they cross a provider-bound auth slice.
 *
 * @example
 * ```ts
 * const identity = AuthIdentitySchema.parse({ id: "u1", roles: ["admin"] });
 * ```
 */
export const AuthIdentitySchema = z.object({
  id: z.string().min(1),
  email: z.string().email().optional(),
  name: z.string().optional(),
  roles: z.array(z.string()).default([]),
  metadata: AdapterMetadataSchema.default({}),
});

/**
 * Identity record used by auth provider adapters.
 *
 * @example
 * ```ts
 * const identity: AuthIdentity = { id: "u1", roles: [], metadata: {} };
 * ```
 */
export type AuthIdentity = z.infer<typeof AuthIdentitySchema>;

/**
 * Session record used by auth provider adapters. Use this schema for session
 * creation and verification results from local/dev auth implementations.
 *
 * @example
 * ```ts
 * const session = AuthSessionContractSchema.parse({ id: "s1", identityId: "u1", token: "token", createdAt: new Date().toISOString() });
 * ```
 */
export const AuthSessionContractSchema = z.object({
  id: z.string().min(1),
  identityId: z.string().min(1),
  token: z.string().min(1),
  expiresAt: IsoDateTimeStringSchema.optional(),
  createdAt: IsoDateTimeStringSchema,
});

/**
 * Session record used by auth provider adapters.
 *
 * @example
 * ```ts
 * const session: AuthSessionContract = { id: "s1", identityId: "u1", token: "token", createdAt: new Date().toISOString() };
 * ```
 */
export type AuthSessionContract = z.infer<typeof AuthSessionContractSchema>;

/**
 * Contract for session and identity providers. Implement this in local auth
 * adapters or downstream provider modules that own auth SDKs.
 *
 * @example
 * ```ts
 * const session = await auth.verifySession(token);
 * ```
 */
export interface AuthProviderAdapter {
  getIdentity(identityId: string): Promise<AuthIdentity | null>;
  createIdentity(identity: AuthIdentity): Promise<AuthIdentity>;
  createSession(identityId: string): Promise<AuthSessionContract>;
  verifySession(token: string): Promise<AuthSessionContract | null>;
  revokeSession(sessionId: string): Promise<void>;
  getHealth(): Promise<AdapterHealth>;
}

/**
 * Payment plan record. Use this schema to validate provider-neutral plan data
 * before displaying pricing or starting checkout.
 *
 * @example
 * ```ts
 * const plan = PaymentPlanContractSchema.parse({ id: "pro", name: "Pro", currency: "USD", amount: 2000, interval: "month" });
 * ```
 */
export const PaymentPlanContractSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  currency: z
    .string()
    .regex(ISO_4217_CURRENCY_CODE, "Expected uppercase ISO 4217 currency code"),
  amount: z.number().int().nonnegative(),
  interval: z.enum(["month", "year", "one-time"]),
  features: z.array(z.string()).default([]),
});

/**
 * Payment plan record.
 *
 * @example
 * ```ts
 * const plan: PaymentPlanContract = { id: "pro", name: "Pro", currency: "USD", amount: 2000, interval: "month", features: [] };
 * ```
 */
export type PaymentPlanContract = z.infer<typeof PaymentPlanContractSchema>;

/**
 * Checkout session creation input. Use this schema before passing checkout
 * requests to a provider-owned payments adapter.
 *
 * @example
 * ```ts
 * const input = PaymentCheckoutInputSchema.parse({ planId: "pro", identityId: "u1" });
 * ```
 */
export const PaymentCheckoutInputSchema = z.object({
  planId: z.string().min(1),
  identityId: z.string().min(1),
  successUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
});

/**
 * Checkout session creation input.
 *
 * @example
 * ```ts
 * const input: PaymentCheckoutInput = { planId: "pro", identityId: "u1" };
 * ```
 */
export type PaymentCheckoutInput = z.infer<typeof PaymentCheckoutInputSchema>;

/**
 * Checkout session record. Use this schema for checkout responses returned by
 * provider-bound payments adapters.
 *
 * @example
 * ```ts
 * const session = PaymentCheckoutSessionSchema.parse({ id: "cs_1", planId: "pro", identityId: "u1", url: "https://pay.example.com", status: "created", createdAt: new Date().toISOString() });
 * ```
 */
export const PaymentCheckoutSessionSchema = z.object({
  id: z.string().min(1),
  planId: z.string().min(1),
  identityId: z.string().min(1),
  url: z.string().url(),
  status: z.enum(["created", "completed", "expired"]),
  createdAt: IsoDateTimeStringSchema,
});

/**
 * Checkout session record.
 *
 * @example
 * ```ts
 * const session: PaymentCheckoutSession = { id: "cs_1", planId: "pro", identityId: "u1", url: "https://pay.example.com", status: "created", createdAt: new Date().toISOString() };
 * ```
 */
export type PaymentCheckoutSession = z.infer<
  typeof PaymentCheckoutSessionSchema
>;

/**
 * Subscription lifecycle record. Use this schema to normalize subscription
 * state from payment providers before checking feature access.
 *
 * @example
 * ```ts
 * const subscription = PaymentSubscriptionContractSchema.parse({ id: "sub_1", planId: "pro", identityId: "u1", status: "active", currentPeriodStart: new Date().toISOString(), currentPeriodEnd: new Date().toISOString(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
 * ```
 */
export const PaymentSubscriptionContractSchema = z.object({
  id: z.string().min(1),
  planId: z.string().min(1),
  identityId: z.string().min(1),
  status: z.enum([
    "active",
    "cancelled",
    "past_due",
    "trialing",
    "incomplete",
    "unpaid",
  ]),
  currentPeriodStart: IsoDateTimeStringSchema,
  currentPeriodEnd: IsoDateTimeStringSchema,
  cancelAtPeriodEnd: z.boolean().default(false),
  cancelledAt: IsoDateTimeStringSchema.optional(),
  createdAt: IsoDateTimeStringSchema,
  updatedAt: IsoDateTimeStringSchema,
  metadata: AdapterMetadataSchema.default({}),
});

/**
 * Subscription lifecycle record.
 *
 * @example
 * ```ts
 * const subscription: PaymentSubscriptionContract = { id: "sub_1", planId: "pro", identityId: "u1", status: "active", currentPeriodStart: new Date().toISOString(), currentPeriodEnd: new Date().toISOString(), cancelAtPeriodEnd: false, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), metadata: {} };
 * ```
 */
export type PaymentSubscriptionContract = z.infer<
  typeof PaymentSubscriptionContractSchema
>;

/**
 * Contract for payment-provider adapters. Implement this in local payment
 * fakes or downstream provider modules that own payment SDKs.
 *
 * @example
 * ```ts
 * const plans = await payments.listPlans();
 * ```
 */
export interface PaymentsProviderAdapter {
  listPlans(): Promise<PaymentPlanContract[]>;
  createCheckout(input: PaymentCheckoutInput): Promise<PaymentCheckoutSession>;
  getSubscription(
    identityId: string,
  ): Promise<PaymentSubscriptionContract | null>;
  cancelSubscription(identityId: string): Promise<void>;
  isFeatureEnabled(identityId: string, feature: string): Promise<boolean>;
  getHealth(): Promise<AdapterHealth>;
}

/**
 * Deployment target configuration. Use this schema before a deploy provider
 * slice accepts app-supplied project and environment metadata.
 *
 * @example
 * ```ts
 * const target = DeploymentTargetSchema.parse({ id: "prod", provider: "cloudflare", projectId: "app", environment: "production" });
 * ```
 */
export const DeploymentTargetSchema = z.object({
  id: z.string().min(1),
  provider: z.enum(["cloudflare", "vercel", "netlify", "heroku"]),
  projectId: z.string().min(1),
  environment: z.string().min(1),
  metadata: AdapterMetadataSchema.default({}),
});

/**
 * Deployment target configuration.
 *
 * @example
 * ```ts
 * const target: DeploymentTarget = { id: "prod", provider: "cloudflare", projectId: "app", environment: "production", metadata: {} };
 * ```
 */
export type DeploymentTarget = z.infer<typeof DeploymentTargetSchema>;

/**
 * Deployment result record. Use this schema to validate deploy, rollback,
 * lookup, and list results returned by provider slices.
 *
 * @example
 * ```ts
 * const result = DeploymentResultSchema.parse({ id: "dep_1", targetId: "prod", status: "ready", createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
 * ```
 */
export const DeploymentResultSchema = z.object({
  id: z.string().min(1),
  targetId: z.string().min(1),
  status: z.enum(["queued", "building", "ready", "failed", "rolled_back"]),
  url: z.string().url().optional(),
  createdAt: IsoDateTimeStringSchema,
  updatedAt: IsoDateTimeStringSchema,
});

/**
 * Deployment result record.
 *
 * @example
 * ```ts
 * const result: DeploymentResult = { id: "dep_1", targetId: "prod", status: "ready", createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
 * ```
 */
export type DeploymentResult = z.infer<typeof DeploymentResultSchema>;

/**
 * Contract for deployment-platform adapters. Implement this in provider slices
 * that own concrete deployment SDKs or app-local deploy wiring.
 *
 * @example
 * ```ts
 * const result = await deploy.deploy(target);
 * ```
 */
export interface DeployAdapter {
  deploy(target: DeploymentTarget): Promise<DeploymentResult>;
  rollback(deploymentId: string): Promise<DeploymentResult>;
  getDeployment(deploymentId: string): Promise<DeploymentResult | null>;
  listDeployments(targetId?: string): Promise<DeploymentResult[]>;
  getHealth(): Promise<AdapterHealth>;
}
